import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, EmptyState, PageHeader, Spinner, TextField } from '../../components/ui';
import { AdminOnly } from './Guard';
import { BASE, CommandArray, DeviceArray, SrDevice, SrPairingCode, keys, useBuildingZone } from './api';

/** The building's shared tablets: pairing code, health, and remote actions. Building administrators only. */
export function DevicesPage() {
  return (
    <AdminOnly>
      <DevicesInner />
    </AdminOnly>
  );
}

function DevicesInner() {
  const zone = useBuildingZone();
  const q = useQuery({ queryKey: keys.devices, queryFn: () => api.get(`${BASE}/devices`, { schema: DeviceArray }) });
  return (
    <>
      <PageHeader title={t('srb.devices.title')} description={t('srb.devices.desc')} />
      <div className="space-y-6">
        <PairingCard />
        {q.isPending ? (
          <Spinner />
        ) : q.isError ? (
          <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
        ) : q.data.filter((d) => d.status === 'active').length === 0 ? (
          <Card>
            <EmptyState message={t('srb.devices.empty')} />
          </Card>
        ) : (
          <ul className="space-y-4" aria-label={t('srb.devices.list')}>
            {q.data
              .filter((d) => d.status === 'active')
              .map((d) => (
                <li key={d.id}>
                  <DeviceCard device={d} zone={zone} />
                </li>
              ))}
          </ul>
        )}
      </div>
    </>
  );
}

function PairingCard() {
  const [name, setName] = useState('');
  const [code, setCode] = useState<SrPairingCode | null>(null);
  const zone = useBuildingZone();
  const create = useMutation({
    mutationFn: () => api.post(`${BASE}/pairing-codes`, { body: { name }, schema: SrPairingCode }),
    onSuccess: (c) => {
      setCode(c);
      setName('');
    },
  });
  const errs = fieldErrors(create.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };
  return (
    <Card title={t('srb.pair.title')} description={t('srb.pair.desc')}>
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-3 sm:items-end" noValidate>
        <TextField label={t('srb.pair.name')} hint={t('srb.pair.nameHint')} value={name} maxLength={60} onChange={(e) => setName(e.target.value)} error={errs['name']} required />
        <div>
          <Button type="submit" variant="primary" disabled={create.isPending || !name.trim()}>
            {t('srb.pair.create')}
          </Button>
        </div>
      </form>
      <ErrorLine error={Object.keys(errs).length ? null : create.error} />
      {code ? (
        <div className="mt-4" role="status">
          <Alert kind="success">
            {t('srb.pair.shown', { time: formatDateTime(code.expiresAt, zone) })}
            <span className="mt-2 block text-3xl font-bold tracking-widest" aria-label={t('srb.pair.code')}>
              {code.code.replace(/(\d{3})(?=\d)/g, '$1 ')}
            </span>
          </Alert>
        </div>
      ) : null}
    </Card>
  );
}

function DeviceCard({ device: d, zone }: { device: SrDevice; zone: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [message, setMessage] = useState('');
  const [showCommands, setShowCommands] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['building', 'devices'] });
  const act = useMutation({
    mutationFn: (body: { action: 'reload_config' | 'restart_app' | 'test_print' } | { action: 'show_message'; message: string; seconds: number }) =>
      api.post(`${BASE}/devices/${d.id}/actions`, { body }),
    onSuccess: async (_r, vars) => {
      if (vars.action === 'show_message') setMessage('');
      await qc.invalidateQueries({ queryKey: keys.commands(d.id) });
      toast(t('srb.devices.sent'));
    },
  });
  const alerts = useMutation({
    mutationFn: (alertsEnabled: boolean) => api.patch(`${BASE}/devices/${d.id}`, { body: { alertsEnabled }, schema: SrDevice }),
    onSuccess: refresh,
  });
  const unpair = useMutation({
    mutationFn: () => api.delete(`${BASE}/devices/${d.id}`),
    onSuccess: async () => {
      await refresh();
      toast(t('srb.devices.unpaired'));
    },
  });
  const commands = useQuery({
    queryKey: keys.commands(d.id),
    queryFn: () => api.get(`${BASE}/devices/${d.id}/commands`, { schema: CommandArray }),
    enabled: showCommands,
  });
  const busy = act.isPending;
  const links = d.links.map((l) => l.displayName ?? '').filter(Boolean);
  // Companies that have not approved this tablet yet: it cannot sign visitors in for them until they do.
  const waiting = d.links.filter((l) => l.approval === 'pending').map((l) => l.displayName ?? '').filter(Boolean);
  return (
    <Card
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          {d.name}
          <Badge tone={d.online ? 'good' : 'warn'}>{d.online ? t('srb.devices.online') : t('srb.devices.offline')}</Badge>
        </span>
      }
      description={[d.model, d.appVersion ? t('srb.devices.version', { version: d.appVersion }) : null].filter(Boolean).join(' - ')}
    >
      <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-slate-600">{t('srb.devices.seen')}</dt>
          <dd>{d.lastSeenAt ? formatDateTime(d.lastSeenAt, zone) : t('srb.devices.never')}</dd>
        </div>
        <div>
          <dt className="text-slate-600">{t('srb.devices.battery')}</dt>
          <dd>{d.health?.batteryPercent != null ? `${d.health.batteryPercent}%${d.health.charging ? ` (${t('srb.devices.charging')})` : ''}` : t('srb.devices.unknown')}</dd>
        </div>
        <div>
          <dt className="text-slate-600">{t('srb.devices.printer')}</dt>
          <dd>{d.printer ? `${d.printer.model} (${d.printer.host})` : t('srb.devices.noPrinter')}</dd>
        </div>
        <div>
          <dt className="text-slate-600">{t('srb.devices.companies')}</dt>
          <dd>{links.length ? links.join(', ') : t('srb.devices.noCompanies')}</dd>
          {waiting.length > 0 ? (
            <dd className="mt-1 text-xs font-medium text-amber-900">
              {t('sr.building.devices.waiting')}: {waiting.join(', ')}
            </dd>
          ) : null}
        </div>
      </dl>
      <div className="mt-4">
        <CheckboxField label={t('srb.devices.alerts')} checked={d.alertsEnabled} disabled={alerts.isPending} onChange={(e) => alerts.mutate(e.target.checked)} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={t('srb.devices.actions', { name: d.name })}>
        <Button disabled={busy} onClick={() => act.mutate({ action: 'reload_config' })}>
          {t('srb.devices.reload')}
        </Button>
        <Button disabled={busy} onClick={() => act.mutate({ action: 'restart_app' })}>
          {t('srb.devices.restart')}
        </Button>
        {d.printer ? (
          <Button disabled={busy} onClick={() => act.mutate({ action: 'test_print' })}>
            {t('srb.devices.testPrint')}
          </Button>
        ) : null}
        <ConfirmButton label={t('srb.devices.unpair')} confirmLabel={t('srb.devices.unpairConfirm', { name: d.name })} pending={unpair.isPending} onConfirm={() => unpair.mutate()} />
      </div>
      <form
        className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          if (message.trim()) act.mutate({ action: 'show_message', message: message.trim(), seconds: 60 });
        }}
      >
        <TextField label={t('srb.devices.message')} hint={t('srb.devices.messageHint')} maxLength={200} value={message} onChange={(e) => setMessage(e.target.value)} />
        <div>
          <Button type="submit" disabled={busy || !message.trim()}>
            {t('srb.devices.showMessage')}
          </Button>
        </div>
      </form>
      <ErrorLine error={act.error ?? alerts.error ?? unpair.error} />
      <div className="mt-4">
        <Button variant="ghost" aria-expanded={showCommands} onClick={() => setShowCommands((v) => !v)}>
          {showCommands ? t('srb.devices.hideCommands') : t('srb.devices.showCommands')}
        </Button>
        {showCommands ? (
          commands.isPending ? (
            <Spinner />
          ) : commands.isError ? (
            <ErrorPanel error={commands.error} onRetry={() => void commands.refetch()} />
          ) : commands.data.length === 0 ? (
            <p className="mt-2 text-sm text-slate-600">{t('srb.devices.noCommands')}</p>
          ) : (
            <ul className="mt-2 divide-y divide-slate-100 text-sm">
              {commands.data.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2 py-2">
                  <span className="font-medium">{t(`srb.devices.action.${c.action}`)}</span>
                  <span className="text-slate-600">{formatDateTime(c.createdAt, zone)}</span>
                  <Badge tone={c.result === 'ok' ? 'good' : c.result === 'failed' ? 'bad' : 'neutral'}>
                    {c.result === 'ok' ? t('srb.devices.result.ok') : c.result === 'failed' ? t('srb.devices.result.failed') : t('srb.devices.result.waiting')}
                  </Badge>
                </li>
              ))}
            </ul>
          )
        ) : null}
      </div>
    </Card>
  );
}
